import { DateTime } from 'luxon';
import { T, always, cond, equals, isNil, or } from 'ramda';
import React, { FC } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow, { IFlexRow } from '@ui/atoms/FlexRow';
import Pill from '@ui/atoms/Pill';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { IFormattedInvoiceProposal, IMemberBill } from '@hooks/useMemberBills';
import { InvoiceStatus, PaymentStatus, RefundStatus } from '@hooks/useMemberInvoice';

import { exists } from '@utils/general';
import { centsToDollars } from '@utils/money';

import { billTypes } from '@data/bills';

import IconArrow from '@assets/vectors/icon-arrow.inline.svg';

import theme from '@theme/FolxTheme';

interface IBillSummaryCardProps {
	bill: IMemberBill;
	className?: string;
	handleClick?: () => void;
}

interface BillDates {
	created: DateTime;
	dueDate?: DateTime | null;
	paidDate?: DateTime | null;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const AlignedRow = fullWidth<IFlexRow>(aligned<IFlexRow>(FlexRow));

const StatusPill = styled(Pill)`
	min-width: ${theme.spacing.xxxl};
	padding: ${theme.spacing.xs} ${theme.spacing.sm};
	width: auto;
`;

const getBillDate = (memberBill: IMemberBill): string => {
	return exists(memberBill?.invoice) && !isNil(memberBill?.invoice)
		? memberBill.invoice.created.toLocaleString()
		: memberBill.created.toLocaleString();
};

const getBillTotal = (memberBill: IMemberBill): string => {
	const total = exists(memberBill?.invoice?.total) ? memberBill?.invoice?.total : memberBill.total;
	return total ? centsToDollars(total) : '';
};

const getBillKind = (memberBill: IMemberBill): keyof typeof billTypes => {
	const isInvoiceProposal = equals('InvoiceProposal', memberBill.__typename);

	return exists(memberBill?.invoice?.kind)
		? memberBill?.invoice?.kind ?? 'uncategorized'
		: exists(memberBill?.kind)
		? memberBill?.kind ?? 'uncategorized'
		: isInvoiceProposal
		? 'medications'
		: 'uncategorized';
};

const getBillId = cond<IMemberBill, boolean, string>([
	[(v: IMemberBill) => exists(v?.invoice), (v: IMemberBill) => v.invoice?.number],
	[(v: IMemberBill) => equals('Invoice', v.__typename), (v: IMemberBill) => v.number],
	[T, always('')],
]);

const hasStatus = (status: InvoiceStatus, memberBill: IMemberBill) =>
	or(
		equals(status, memberBill?.status),
		equals(status, (memberBill as unknown as IFormattedInvoiceProposal)?.invoice?.status)
	);

const getOpenStatus = cond<keyof typeof billTypes, boolean, string>([
	[
		equals('medications'),
		always(<StatusPill color="beige" variant="fineprintBold" title="Unpaid" />),
	],
	[T, always(<StatusPill color="lightRed" variant="fineprintBold" title="Failed" />)],
]);

const getBillStatus = (bill: IMemberBill, billKind: keyof typeof billTypes) =>
	cond<IMemberBill, boolean, string>([
		// [
		// 	(v: IMemberBill) =>
		// 		or(
		// 			equals(RefundStatus.refunded, v?.refundStatus),
		// 			equals(
		// 				RefundStatus.refunded,
		// 				(v as unknown as IFormattedInvoiceProposal)?.invoice?.refundStatus
		// 			)
		// 		),
		// 	always(<StatusPill color="lightCyan" variant="fineprintBold" title="Refunded" />),
		// ],
		[
			(v: IMemberBill) =>
				or(
					equals(PaymentStatus.canceled, v?.paymentStatus),
					equals(
						PaymentStatus.canceled,
						(v as unknown as IFormattedInvoiceProposal)?.invoice?.paymentStatus
					)
				),
			always(<StatusPill color="beige" variant="fineprintBold" title="Canceled" />),
		],
		[
			(v: IMemberBill) => hasStatus(InvoiceStatus.paid, v),
			always(<StatusPill color="green" variant="fineprintBold" title="Paid" />),
		],
		[(v: IMemberBill) => hasStatus(InvoiceStatus.open, v), () => getOpenStatus(billKind)],
		[
			(v: IMemberBill) => hasStatus(InvoiceStatus.uncollectible, v),
			always(<StatusPill color="lightRed" variant="fineprintBold" title="Expired" />),
		],
		[
			(v: IMemberBill) => hasStatus(InvoiceStatus.void, v),
			always(<StatusPill color="beige" variant="fineprintBold" title="Canceled" />),
		],
		[T, always(<StatusPill color="gray" variant="fineprintBold" title="Unknown" />)],
	])(bill);

const BillSummary = ({ bill }: IBillSummaryCardProps) => {
	const billDate = getBillDate(bill);
	const billTotal = getBillTotal(bill);
	const billId = getBillId(bill);
	const billKind = getBillKind(bill);
	const billStatus = getBillStatus(bill, billKind);

	return (
		<FullWidthColumn gap="xs">
			<AlignedRow justifyContent="space-between">
				<BasicText>{billDate}</BasicText>
				{billStatus}
			</AlignedRow>
			<BasicText variant="field">{billTotal}</BasicText>
			<FullWidthColumn>
				<BasicText variant="fineprint">{billTypes[billKind]}</BasicText>
				{exists(billId) && <BasicText variant="fineprint">{billId}</BasicText>}
			</FullWidthColumn>
		</FullWidthColumn>
	);
};

const BillSummaryCard: FC<IBillSummaryCardProps> = ({
	bill,
	className,
	handleClick = () => {
		return;
	},
}) => {
	return (
		<BaseCard
			padding="smmd"
			className={className}
			bgColor="white"
			borderless
			clickable
			radius="md"
			shadow
			onClick={handleClick}>
			<AlignedRow alignItems="center" gap="sm">
				<BillSummary bill={bill} />
				<IconArrow style={{ width: theme.spacing.lg, height: theme.spacing.lg }} />
			</AlignedRow>
		</BaseCard>
	);
};

export default styled(BillSummaryCard)``;
